import React, { useState, useCallback, useMemo } from 'react';
import { CLIP_STATUS } from 'engage/constants/projectCreatorConsts';
import VideoService from 'engage/service/VideoService';
import ResourceHelper from 'engage/utils/ResourceHelper';
import { arrayMove } from 'react-sortable-hoc';
import { v4 as uuidv4 } from 'uuid';
import { ScenesType } from 'common/apis/CmsAPI';
import { useTranslation } from 'react-i18next';
import { ToastNotification } from 'engage/components/Toast/Toast';
import * as Sentry from '@sentry/react';
import CmsAPI from 'common/apis/CmsAPI';
import { useUserContext } from 'engage/context/UserContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';


const unsupportedKeys = ['hidden', 'processing', 'recording', 'recorded', 'reuploaded'];

/**
 * Custom hook to manage scenes in a storyboard.
 */

export const useStoryScenes = ({ videoId, video, assignments, permissions, userPermissions }) => {

    const [scenes, setScenes] = useState([]);
    const [currentScene, setCurrentScene] = useState('0');
    const [initJobs, setInitJobs] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user } = useUserContext();
    const history = useHistory();

    const { t } = useTranslation();

    const permittedScenes = useMemo(() => {
        return scenes.map(scene => {
            if (!scene?.author) {
                let status = CLIP_STATUS.NOT_ASSIGNED.TYPE;
                if (scene?.video) {
                    status = CLIP_STATUS.RECORDED.TYPE;
                }

                const assignment = assignments.find(item => scene.id === item.sceneId);
                if (assignment) {
                    if (scene.video === null) {
                        status = assignment.readAt === null ? CLIP_STATUS.NOT_RESPONDED.TYPE : CLIP_STATUS.PENDING.TYPE;
                    }
                    const userPermissions = permissions.find(item => item.username === assignment.assigned.username);
                    const handledPermissions = { read: false, update: false };
                    if (userPermissions?.storyPermissions?.length) {
                        handledPermissions.read = userPermissions.storyPermissions?.findIndex(item => item.action === 'read') >= 0;
                        handledPermissions.update = userPermissions.storyPermissions?.findIndex(item => item.action === 'update') >= 0;
                    }
                    return { ...structuredClone(scene), author: assignment.assigned, permissions: handledPermissions, status };
                } else {
                    return { ...structuredClone(scene), author: video?.author, status };
                }
            }
            return scene;
        });
    }, [scenes,
        video,
        assignments,
        permissions
    ]);

    const initScenes = useCallback(async (scenes = [], requestJobs = true) => {
        const { readStory, updateStory, allowedScenes } = userPermissions;
        if (scenes.length === 0) {
            // No init scenes
            Sentry.captureException(new Error('No scenes provided for initialization. userId: ', user.username, ' videoId: ', videoId, '. User permissions: ', userPermissions, '. User will be redirected to home.'));
            history.push('/eg/home/');
            return;
        }
        const scenesToOperate =
            (readStory || updateStory
                ? structuredClone(scenes)
                : scenes.filter(item => allowedScenes.find(allowed => item.id === allowed))).sort((a, b) => a.sort - b.sort);

        if (requestJobs) {
            const storyJobs = await VideoService.checkSceneJobs(videoId);
            const runningJobs = [];
            const checkedScenes = await Promise.all(scenesToOperate.map(async scene => {
                if (!ResourceHelper.getResourceByLabel(scene, 'hls') || ResourceHelper.getResourceByLabel(scene, 'avatar') || ResourceHelper.getResourceByLabel(scene, 'enhanced')) {
                    if (storyJobs.hasRunningJobs) {
                        const sceneJobs = storyJobs.runningJobs.filter(({ outputs }) => outputs[0].relatedTo.id == scene.id && outputs.filter(output => output.label != 'hlsTrimmed').length > 0);

                        if (sceneJobs.length) {
                            runningJobs.push(...sceneJobs.map(item => item.jobId));
                            scene.processing = true;
                        }
                    }
                }
                return scene;
            }));
            if (runningJobs.length > 0) {
                setInitJobs(runningJobs);
            }
            setScenes(checkedScenes);
            return;
        }
        const mappedScenes = scenesToOperate.map(scene => {
            const oldSceneData = scenes.find(item => item.id === scene.id) ?? {};
            return { ...oldSceneData, ...scene };
        });
        setScenes(mappedScenes);
    }, [userPermissions, scenes, videoId, setScenes]);

    const addScene = async (index, before, orientation = null, data = {}) => {
        try {
            const id = data?.id ?? uuidv4();
            const { result: scenes } = await VideoService.createScene({
                id,
                storyId: videoId,
                sort: before ? index : index + 1,
                name: data?.name ?? `${t('storyboard.editor.default_name')} ${before ? index + 1 : index + 2}`,
                script: data?.script ?? '',
                narrative: data?.narrative ?? '',
                orientation,
            });
            initScenes(scenes, false);
            setCurrentScene((before ? index : index + 1).toString());
            !data && ToastNotification({ message: t('storyboard.editor.new_scene_added'), iconName: 'success' });
            return id;
        } catch (e) {
            console.log('Error while scene duplication', e);
        }
    };

    const duplicateScene = useCallback(async id => {
        const sceneToDuplicate = scenes.find(item => item.id === id);
        const newId = uuidv4();
        if (sceneToDuplicate) {
            setScenes(prevState => [...prevState, { ...sceneToDuplicate, id: newId, processing: true }]);
            try {
                const { result: scenes } = await VideoService.createScene({
                    id: newId,
                    storyId: videoId,
                    name: sceneToDuplicate.name,
                    script: sceneToDuplicate.script,
                    narrative: sceneToDuplicate.narrative,
                });
                initScenes(scenes, false);
                ToastNotification({ message: t('storyboard.editor.scene_duplicated'), iconName: 'success' });
            } catch (e) {
                console.log('Error while scene duplication', e);
            }
        }
    }, [initScenes]);

    const deleteScene = useCallback(
        async (id, confirmation = true) => {
            const sceneToDelete = scenes?.find(item => item.id === id);
            if (!confirmation || confirm(`${t('general.confirm.delete')} '${sceneToDelete?.name}'?`)) {
                try {
                    setScenes(prevState => prevState?.filter(item => item.id !== sceneToDelete.id));
                    const { result: scenes } = await VideoService.deleteScene(id);
                    const message = t('storyboard.editor.scene_deleted', { name: sceneToDelete.name });

                    if (sceneToDelete.sort === Number(currentScene) && sceneToDelete.sort === scenes.length - 1) {
                        setCurrentScene(`${scenes.length - 1}`);
                    }

                    if (Number(currentScene) > sceneToDelete.sort) {
                        setCurrentScene(`${Number(currentScene) - 1}`);
                    }

                    confirmation && ToastNotification({ message, iconName: 'success' });
                } catch (e) {
                    const message = t('storyboard.editor.scene_deleted.error', { name: sceneToDelete.name });
                    setScenes(prevState => structuredClone(prevState).splice(sceneToDelete.sort, 0, sceneToDelete));
                    confirmation && ToastNotification({ message });
                    console.log('Error while removing scene', e);
                }
            }
        },
        [currentScene, scenes, setScenes]
    );

    const changeScenesOrientation = useCallback(
        async newOrientation => {
            const scenesToUpdate = scenes.map(scene => ({ storyId: videoId, id: scene.id, orientation: newOrientation }));
            const { result: updatedScenes } = await CmsAPI.bulkUpdateScenes(scenesToUpdate);
            if (updatedScenes?.length) {
                initScenes(updatedScenes, false);
            }
        },
        [scenes, videoId, initScenes]
    );


    const moveScene = useCallback(
        async ({ oldIndex, newIndex }) => {
            if (oldIndex !== newIndex) {
                const activeClip = scenes[Number(currentScene)];
                const movedArray = arrayMove(scenes, oldIndex, newIndex);
                const updatedIndex = movedArray.indexOf(activeClip);
                setScenes(movedArray);
                setCurrentScene(updatedIndex.toString());
                await VideoService.updateVideo(videoId, { scenes: movedArray });
            }
        },
        [scenes, currentScene, setScenes, setCurrentScene, videoId]
    );

    const changeScene = useCallback(
        async (id, changes) => {
            if (Object.keys(changes).length == 0 || !id) {
                return;
            }
            // Define if changes should be sent to API
            const apiChanges = {};
            for (const key in changes) {
                if (!unsupportedKeys.includes(key)) {
                    apiChanges[key] = changes[key];
                }
            }
            setScenes(prevState =>
                prevState.map(item => {
                    if (item.id === id) {
                        return { ...item, ...changes };
                    }
                    return item;
                })
            );

            if (Object.keys(apiChanges).length > 0) {
                try {
                    await VideoService.updateScene(id, { ...apiChanges, storyId: videoId });
                } catch (e) {
                    console.log(`Update changes ${changes} for scene ${id} failed with code`, e);
                }
            }
        },
        [videoId, scenes, setScenes]
    );

    const requestSceneUpdate = async (sceneId, force = false) => {
        const updatedDetails = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
        setScenes(prevState => prevState.map(item => {
            if (item.id === sceneId) {
                const sceneData = updatedDetails.scenes.find(scene => scene.id === item.id);
                if (sceneData) {
                    const needsUpdate = item.rerecorded || !item.trimmedDuration || item.trimmedDuration !== sceneData.trimmedDuration || !item.orientation;
                    if (sceneData && (needsUpdate || force)) {
                        const hlsResource = ResourceHelper.getResourceByLabel(sceneData, 'hls');
                        const thumbnailResource = ResourceHelper.getResourceByLabel(sceneData, 'defaultStaticThumbnail');
                        return {
                            ...item,
                            processing: false,
                            resources: sceneData.resources,
                            video: hlsResource ? `${hlsResource?.url}?${Date.now()}` : null,
                            trimmedDuration: sceneData?.trimmedDuration,
                            trimStart: 0,
                            trimEnd: sceneData?.trimmedDuration,
                            orientation: sceneData?.orientation,
                            reuploaded: false,
                            thumbnail: thumbnailResource ? `${thumbnailResource?.url}?${Date.now()}` : null,
                        };
                    }
                }
            }
            return item;
        }));
    };

    const copyScenesIntoStory = useCallback(
        async (duplicateFrom, index = 0, before = false) => {
            // Relying on index & before create an array of new scenes
            const scenesForRequest = scenes.map(scene => ({
                id: scene.id,
                name: scene.name,
            }));
            setLoading(true);
            try {
                const sliceIndex = before ? index : index + 1;
                const duplicatedScenes = duplicateFrom.map(id => ({
                    id,
                    newSceneId: uuidv4(),
                    name: `${t('storyboard.editor.default_name')} ${sliceIndex + 1}`,
                }));
                scenesForRequest.splice(sliceIndex, 0, ...duplicatedScenes)
                if (scenesForRequest.length === 0) {
                    Sentry.captureException(new Error('Empty scenes array when copying scenes into story'));
                    return;
                }
                // This request takes a while, so for user not to be confused, add a 'fake' scenes to indicate processing
                setScenes(prevState => {
                    const newScenes = [...prevState];
                    const fakeScenes = duplicateFrom.map(id => ({
                        id,
                        processing: true,
                        name: `${t('storyboard.editor.default_name')} ${before ? index + 1 : index + 2}`,
                        dimensions: { width: 0, height: 0 },
                        orientation: null,
                        video: null,
                        trimmedDuration: 0,
                        trimStart: 0,
                        trimEnd: 0,
                        resources: [],
                    }));
                    newScenes.splice(before ? index : index + 1, 0, ...fakeScenes);
                    return newScenes;
                });
                await VideoService.updateVideo(video.id, { scenes: scenesForRequest });
                const videoDetails = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
                initScenes(videoDetails.scenes, true);
            } catch (e) {
                ToastNotification({ message: t('storyboard.editor.copy_scenes.error'), iconName: 'error' });
            } finally {
                setLoading(false);
            }
        },
        [scenes]
    );

    const initJobsAdded = () => {
        setInitJobs(false);
    }

    const selectScene = value => {
        setCurrentScene(value);
    };
    const result = useMemo(() => {
        return {
            scenes: permittedScenes,
            currentScene,
            permittedScenes,
            currentScene,
            initJobs, loading,
            initScenes,
            addScene,
            duplicateScene,
            copyScenesIntoStory,
            deleteScene,
            moveScene,
            selectScene,
            changeScenesOrientation,
            changeScene,
            requestSceneUpdate,
            initJobsAdded
        }
    }, [permittedScenes,
        currentScene,
        initJobs,
        loading,
        initScenes,
        addScene,
        duplicateScene,
        copyScenesIntoStory,
        deleteScene,
        moveScene,
        selectScene,
        changeScenesOrientation,
        changeScene,
        requestSceneUpdate,
        initJobsAdded]);

    return result;
}